import { createFileRoute, redirect, useNavigate, Outlet, useMatches } from '@tanstack/react-router';
import { m } from 'framer-motion';
import { useEffect, useState, useRef, useCallback } from 'react';
import styles from './login.module.less';
import { TelegramWarningModal, LoginWelcomeScreen, LoginChallengeScreen } from '@/components/auth';
import { SessionRecoveryAlert } from '@/components/auth/session-recovery-alert';
import { useTelegramAuthInit, useTelegramAuthVerify, useSession, useUrlAuthEffect } from '@/hooks';
import { safeRedirect } from '@/hooks/useUrlAuthEffect';
import { getOnboardingPreloadLinks } from '@/components/v2/onboarding/onboarding-assets';
import { getAssetUrl } from '@/lib/utils/assets';
import { logger } from '@/lib/utils/logger';
import {
  navigateToPostLoginDestination,
  resolveLoginRoute,
  resolvePostLoginDestination,
} from '@/lib/auth/route-auth-decisions';

type LoginSearch = {
  auth?: string;
  code?: string;
} & Record<string, unknown>;

// Hand-rolled like _public/class/$classId.tsx and routes/index.tsx: this
// route-def file is in the eager chunk, and a zod import here pinned all of
// zod into the root preload set. Mirrors the previous
// z.object({ auth: z.string().optional().catch(undefined), code: z.coerce.string().optional().catch(undefined) }).passthrough():
// - auth keeps only genuine strings; anything else falls back to absent,
//   exactly as .catch(undefined) did.
// - code coerces any defined value with String(), exactly like
//   z.coerce.string() (magic-link codes arrive JSON-parsed as numbers).
// - every other param (notably `redirect`) passes through untouched, like
//   .passthrough() did.
const parseLoginSearch = (search: Record<string, unknown>): LoginSearch => {
  const parsed: LoginSearch = { ...search };
  if (typeof search.auth === 'string') {
    parsed.auth = search.auth;
  } else {
    delete parsed.auth;
  }
  if (search.code !== undefined) {
    parsed.code = String(search.code);
  } else {
    delete parsed.code;
  }
  return parsed;
};

// Hero img and its preload hint are built from the SAME srcset+sizes: the
// browser then preloads exactly the variant it paints on every viewport, so
// the preload is always consumed. A plain-href preload mismatches the srcset
// pick and logs "preloaded but not used" (same fix as onboarding-assets.ts).
const BACKDROP_HERO_SRCSET = `${getAssetUrl('/placeholders/homepage/singapore-backdrop-828w.webp')} 828w, ${getAssetUrl('/placeholders/homepage/singapore-backdrop.webp')} 1849w`;
const BACKDROP_HERO_SIZES = '100vw';

// Scoped to /login only: the backdrop is rendered here and nowhere else;
// a root-level preload warns on every post-login page load.
export const getBackdropPreloadLinks = () => [
  {
    rel: 'preload',
    as: 'image',
    imageSrcSet: BACKDROP_HERO_SRCSET,
    imageSizes: BACKDROP_HERO_SIZES,
    fetchPriority: 'high' as const,
  },
];

export const Route = createFileRoute('/login')({
  validateSearch: parseLoginSearch,
  head: () => ({
    // Preload the first onboarding slide and prefetch the rest while the
    // learner is still on login, so first-login onboarding renders with no
    // visible image loads (#410, #653).
    links: [...getBackdropPreloadLinks(), ...getOnboardingPreloadLinks()],
  }),
  beforeLoad: ({ context, search, location }) => {
    const typedSearch = search as LoginSearch & { redirect?: string };
    const sourceOrigin =
      typeof window === 'undefined' ? 'http://localhost' : window.location.origin;
    const destination = resolvePostLoginDestination(
      typedSearch.redirect,
      sourceOrigin,
      safeRedirect(typedSearch.redirect, sourceOrigin)
    );
    const decision = resolveLoginRoute(
      context.authState,
      typedSearch,
      location.pathname,
      destination.kind === 'internal' ? destination.to : '/home'
    );
    if (decision.kind === 'redirect') {
      if (typeof window === 'undefined' && typedSearch.redirect) {
        try {
          const requested = new URL(typedSearch.redirect, sourceOrigin);
          if (requested.origin !== sourceOrigin) return { showRecovery: false };
        } catch {
          // Invalid redirects keep the existing internal fallback.
        }
      }
      if (destination.kind === 'external') {
        if (typeof window !== 'undefined') {
          navigateToPostLoginDestination(destination, {
            assign: (url) => window.location.assign(url),
            navigate: () => undefined,
          });
        }
        return { showRecovery: false };
      }
      logger.info('LoginRoute', 'Authenticated session present', {
        destination: decision.to,
      });
      throw redirect({ to: decision.to });
    }
    return { showRecovery: decision.kind === 'recovery' };
  },
  component: LoginPage,
});

function LoginPage() {
  const navigate = useNavigate();
  const matches = useMatches();
  const { showRecovery } = Route.useRouteContext();

  // ALL hooks must be called before any conditional returns (React hooks rules)
  // Use validated search params from route to avoid type issues
  // SAFETY: the login route declares no validateSearch, so useSearch is typed
  // unknown; the magic-link params are optional strings the code guards below.
  const search = Route.useSearch() as unknown as {
    auth?: string;
    code?: string;
    redirect?: string;
  };

  const [challengeCode, setChallengeCode] = useState('');
  const [step, setStep] = useState<'init' | 'challenge'>('init');
  // UIUX-11: consent/code errors BLOCK the form (checkbox unchecked, continue
  // inert), so they must persist until the offending input changes; transient
  // network/verify errors auto-dismiss. `error` stays a string for the screens.
  const [errorState, setErrorState] = useState<{ message: string; blocking: boolean } | null>(null);
  const error = errorState?.message ?? null;
  const setError = useCallback((message: string | null) => {
    setErrorState(message ? { message, blocking: false } : null);
  }, []);
  const setBlockingError = useCallback((message: string) => {
    setErrorState({ message, blocking: true });
  }, []);

  const [authUrl, setAuthUrl] = useState<string | null>(null);
  const [authCode, setAuthCode] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [agreeToTerms, setAgreeToTerms] = useState(false);
  const [allowCookies, setAllowCookies] = useState(false);
  const [shakeCheckboxes, setShakeCheckboxes] = useState(false);
  const autoSubmitRef = useRef(false);

  const initAuth = useTelegramAuthInit();
  const verifyAuth = useTelegramAuthVerify();
  const sessionQuery = useSession(); // Check if user is already logged in

  // Handle URL-based authentication (magic link flow)
  useUrlAuthEffect({
    search,
    allowCookies,
    verifyAuth,
    setAuthCode,
    setChallengeCode,
    setStep,
    setError,
    setShakeCheckboxes,
  });

  // Check if we're rendering a child route (like /login/admin)
  // useMatches() returns ALL matched routes from root to current:
  // - /login: [__root__, /login] = 2 matches
  // - /login/admin: [__root__, /login, /login/admin] = 3 matches
  // So we need to check if there's MORE than root + login (i.e., > 2)
  const hasChildRoute = matches.length > 2;

  // Get redirect destination from URL params, fallback to /home
  const getRedirectDestination = useCallback(
    () =>
      resolvePostLoginDestination(
        search.redirect,
        window.location.origin,
        safeRedirect(search.redirect)
      ),
    [search.redirect]
  );

  // Handle challenge form submission
  const handleChallengeSubmit = useCallback(
    async (e?: React.FormEvent) => {
      if (e) {
        e.preventDefault();
      }

      if (!allowCookies) {
        setBlockingError('Please allow cookie usage to continue.');
        setShakeCheckboxes(true);
        setTimeout(() => setShakeCheckboxes(false), 500);
        return;
      }

      if (verifyAuth.isPending) {
        return;
      }

      setError(null);

      if (!authCode) {
        setError('Authentication session expired. Please start over.');
        setStep('init');
        return;
      }

      const normalizedCode = challengeCode.trim();

      if (normalizedCode.length !== 6) {
        setBlockingError('Challenge code must be 6 digits');
        return;
      }

      logger.info('[Login]', 'Verification', {
        hasAuthCode: !!authCode,
        challengeCodeLength: normalizedCode.length,
      });

      try {
        await verifyAuth.mutateAsync({
          auth_code: authCode,
          challenge_code: normalizedCode,
        });

        const destination = getRedirectDestination();
        navigateToPostLoginDestination(destination, {
          assign: (url) => window.location.assign(url),
          navigate: (to) => navigate({ to }),
        });
      } catch (err) {
        logger.error('[Login]', err);
        const errorMessage =
          err instanceof Error
            ? err.message
            : typeof err === 'string'
              ? err
              : 'Failed to verify. Please try again.';
        setError(errorMessage);
      }
    },
    [allowCookies, verifyAuth, authCode, challengeCode, navigate, getRedirectDestination]
  );

  // ALL useEffect hooks must be called BEFORE any conditional returns (React Hooks Rules)
  useEffect(() => {
    logger.info('[Login]', 'Search params received', {
      redirect: search.redirect,
      hasAuth: !!search.auth,
      hasCode: !!search.code,
      hasRedirect: !!search.redirect,
    });
    return undefined;
  }, [search.auth, search.code, search.redirect]);

  // Handle mutation errors
  useEffect(() => {
    if (initAuth.isError) {
      const error = initAuth.error as Error | string | null | undefined;
      const errorMessage =
        error instanceof Error
          ? error.message
          : typeof error === 'string'
            ? error
            : 'Failed to connect to authentication service. Please check your connection and try again.';
      setError(errorMessage);
      setStep('init');
    }
    return undefined;
  }, [initAuth.isError, initAuth.error]);

  useEffect(() => {
    if (verifyAuth.isError) {
      const error = verifyAuth.error as Error | string | null | undefined;
      const errorMessage =
        error instanceof Error
          ? error.message
          : typeof error === 'string'
            ? error
            : 'Failed to verify authentication. Please try again.';
      setError(errorMessage);
    }
    return undefined;
  }, [verifyAuth.isError, verifyAuth.error]);

  // Redirect if already authenticated - use session query instead of token check
  useEffect(() => {
    // Skip if processing URL params (magic link flow)
    if (search.auth && search.code) {
      return undefined;
    }

    // Wait for session query to complete, then redirect if authenticated
    if (!sessionQuery.isLoading && sessionQuery.data) {
      const destination = getRedirectDestination();
      logger.info('[Login]', 'Valid session found, redirecting to', {
        destination,
      });
      navigateToPostLoginDestination(destination, {
        assign: (url) => window.location.assign(url),
        navigate: (to) => navigate({ to }),
      });
    }
    return undefined;
  }, [navigate, search.auth, search.code, sessionQuery.isLoading, sessionQuery.data]);

  // Auto-dismiss transient errors only (UIUX-11: no earlier than 10s).
  // Blocking validation errors persist: the 5s timer used to erase them while
  // the checkbox stayed unchecked and the continue button inert.
  useEffect(() => {
    if (errorState && !errorState.blocking) {
      const timer = setTimeout(() => setErrorState(null), 10000);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [errorState]);

  // Blocking errors clear the moment the user changes an input that can
  // unblock them (toggle a consent box, edit the code).
  useEffect(() => {
    setErrorState(null);
  }, [agreeToTerms, allowCookies, challengeCode]);

  // Auto-submit when 6 digits are entered
  useEffect(() => {
    if (
      challengeCode.length === 6 &&
      step === 'challenge' &&
      authCode &&
      !verifyAuth.isPending &&
      !verifyAuth.isError &&
      !autoSubmitRef.current
    ) {
      const normalizedCode = challengeCode.trim();
      if (normalizedCode.length === 6) {
        autoSubmitRef.current = true;
        const timeoutId = setTimeout(() => {
          handleChallengeSubmit();
          setTimeout(() => {
            autoSubmitRef.current = false;
          }, 1000);
        }, 100);
        return () => clearTimeout(timeoutId);
      }
    }
    if (challengeCode.length !== 6 || step !== 'challenge') {
      autoSubmitRef.current = false;
    }

    return undefined;
  }, [
    challengeCode,
    step,
    authCode,
    verifyAuth.isPending,
    verifyAuth.isError,
    handleChallengeSubmit,
  ]);

  // If there's a child route, render it instead of the login form
  // This check must come AFTER all hooks are called
  if (hasChildRoute) {
    return <Outlet />;
  }

  if (showRecovery) {
    return <SessionRecoveryAlert />;
  }

  const handleLoginClick = () => {
    if (!agreeToTerms) {
      setBlockingError('Please agree to the Terms of Service and Privacy Policy.');
      setShakeCheckboxes(true);
      setTimeout(() => setShakeCheckboxes(false), 500);
      return;
    }

    if (!allowCookies) {
      setBlockingError('Please allow cookie usage to continue.');
      setShakeCheckboxes(true);
      setTimeout(() => setShakeCheckboxes(false), 500);
      return;
    }
    setError(null);
    setShowModal(true);
  };

  const handleStartAuth = async () => {
    setError(null);
    setShowModal(false);

    try {
      const result = await initAuth.mutateAsync();

      if (!result.auth_url) {
        throw new Error('Missing auth_url in response');
      }

      setAuthUrl(result.auth_url);
      setAuthCode(result.auth_code);

      if (result.auth_url) {
        window.open(result.auth_url, '_blank', 'noopener,noreferrer');
      }

      setStep('challenge');
    } catch (err) {
      logger.error('[Login]', err);
      const errorMessage =
        err instanceof Error
          ? err.message
          : typeof err === 'string'
            ? err
            : 'Failed to authenticate. Please try again.';
      setError(errorMessage);
      setStep('init');
    }
  };

  const handleOpenTelegram = () => {
    if (authUrl) {
      window.open(authUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const handleBack = () => {
    setStep('init');
  };

  return (
    <div className={styles.loginPage}>
      {/* Hero Image - Side Panel (Desktop) / Top (Mobile) */}
      <m.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
        className={`${styles.heroImageContainer} ${step === 'challenge' ? styles.challengeMode : ''}`}
      >
        <img
          src={getAssetUrl('/placeholders/homepage/singapore-backdrop.webp')}
          srcSet={BACKDROP_HERO_SRCSET}
          sizes={BACKDROP_HERO_SIZES}
          alt="Singapore Backdrop"
          className={styles.heroImage}
        />
      </m.div>

      {/* Step 1 - Welcome Screen */}
      {step === 'init' && (
        <LoginWelcomeScreen
          error={error}
          agreeToTerms={agreeToTerms}
          allowCookies={allowCookies}
          shakeCheckboxes={shakeCheckboxes}
          isLoading={initAuth.isPending}
          onAgreeToTermsChange={setAgreeToTerms}
          onAllowCookiesChange={setAllowCookies}
          onLoginClick={handleLoginClick}
        />
      )}

      {/* Step 2 - Challenge Code Entry */}
      {step === 'challenge' && (
        <LoginChallengeScreen
          error={error}
          challengeCode={challengeCode}
          allowCookies={allowCookies}
          shakeCheckboxes={shakeCheckboxes}
          isVerifying={verifyAuth.isPending}
          authUrl={authUrl}
          onChallengeCodeChange={setChallengeCode}
          onAllowCookiesChange={setAllowCookies}
          onBack={handleBack}
          onSubmit={handleChallengeSubmit}
          onOpenTelegram={handleOpenTelegram}
        />
      )}

      {/* Telegram Warning Modal */}
      <TelegramWarningModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        onContinue={handleStartAuth}
      />
    </div>
  );
}
